import React, { useState, useEffect } from 'react';
import { ParkingPricingAssumptions } from '../../types/policyImpact';
import { supabaseClient } from '../../services/supabase';
import './ParkingPricingPolicy.css';

interface ParkingPricingPolicyProps {
  assumptions: ParkingPricingAssumptions;
  onChange: (assumptions: ParkingPricingAssumptions) => void;
  currentVMT: number; // Current VMT for the location
  projectedBAUVMT: number; // Projected BAU VMT
  location: string; // Location name
  geofips: number | null; // Geographic identifier
}

const ParkingPricingPolicy: React.FC<ParkingPricingPolicyProps> = ({
  assumptions,
  onChange,
  currentVMT,
  projectedBAUVMT,
  location,
  geofips,
}) => {
  const [actualCommutePercent, setActualCommutePercent] = useState<number | null>(null);

  const SF_COMMUTE_BY_CAR_PERCENT = 30.29; // San Francisco baseline
  const BASE_ELASTICITY = -0.4; // Pierce and Shoup 2013

  // Fetch actual commute-by-car ("drove alone") percentage for the selected location (2023)
  useEffect(() => {
    const fetchCommuteData = async () => {
      if (geofips === null || geofips === undefined) return;

      // Ensure numeric geofips for the query
      const numericGeofips = typeof geofips === 'string' ? Number.parseInt(geofips as unknown as string, 10) : geofips;
      if (!numericGeofips || Number.isNaN(numericGeofips)) return;

      try {
        // Primary source: city/place-level share that "Drove alone" from means_of_transportation_to_work
        const { data: placeRows, error: placeErr } = await supabaseClient
          .from('means_of_transportation_to_work')
          .select('percent, means_of_transportation')
          .eq('geofips', numericGeofips)
          .eq('geography_type', 'place')
          .eq('year', 2023)
          .ilike('means_of_transportation', '%Drove alone%')
          .order('percent', { ascending: false })
          .limit(1);

        if (!placeErr && placeRows && placeRows.length > 0) {
          const pct = placeRows[0].percent as number;
          setActualCommutePercent(pct);
          onChange({ ...assumptions, commuteByCarPercent: pct });
          return;
        }

        // Fallback: county series table percent_drove_alone_work (in case place data not available)
        const { data, error } = await supabaseClient
          .from('percent_drove_alone_work')
          .select('percent_drove_alone')
          .eq('geofips', numericGeofips)
          .eq('year', 2023)
          .limit(1);

        if (!error && data && data.length > 0) {
          const pct = data[0].percent_drove_alone as number;
          setActualCommutePercent(pct);
          onChange({ ...assumptions, commuteByCarPercent: pct });
        }
      } catch (err) {
        console.error('Error fetching commute data:', err);
      }
    };

    fetchCommuteData();
  }, [geofips, location, assumptions, onChange]);

  // Calculate elasticity based on commute by car percentage
  const calculateElasticity = () => {
    if (assumptions.commuteByCarPercent >= 100 || assumptions.commuteByCarPercent === 0) {
      return 0; // No effect if 100% commute by car or 0%
    }
    return BASE_ELASTICITY * (SF_COMMUTE_BY_CAR_PERCENT / assumptions.commuteByCarPercent);
  };

  const elasticity = calculateElasticity();

  // Calculate VMT reduction from parking pricing
  const calculateVMTReduction = () => {
    if (assumptions.priceIncreasePercent === 0) return 0;
    
    // Calculate percent VMT reduction based on elasticity and price change
    const vmtReductionPercent = Math.abs(elasticity) * (assumptions.priceIncreasePercent / 100);
    
    // Apply only to the percentage of VMT that can be affected
    const effectiveReductionPercent = vmtReductionPercent * (assumptions.vmtAffectedPercent / 100);
    
    // Calculate VMT reduction in miles
    const vmtAffected = projectedBAUVMT * (assumptions.vmtAffectedPercent / 100);
    const vmtReduction = vmtAffected * effectiveReductionPercent;
    
    return {
      percentReduction: effectiveReductionPercent * 100,
      vmtReduction,
    };
  };

  const vmtResults = calculateVMTReduction();
  const defaultResults = { percentReduction: 0, vmtReduction: 0 };
  const finalResults = typeof vmtResults === 'object' ? vmtResults : defaultResults;

  const handleChange = (field: keyof ParkingPricingAssumptions, value: number) => {
    onChange({
      ...assumptions,
      [field]: value,
    });
  };

  return (
    <div className="parking-pricing-policy">
      {/* Main Parking Pricing Slider */}
      <div className="policy-main-control-section">
        <label className="policy-main-label">
          Parking Pricing Increase
        </label>
        <div className="policy-main-slider-container">
          <input
            type="range"
            min={0}
            max={300}
            step={10}
            value={assumptions.priceIncreasePercent}
            onChange={(e) => handleChange('priceIncreasePercent', Number(e.target.value))}
            className="policy-main-slider"
          />
          <div className="policy-main-value">{assumptions.priceIncreasePercent}%</div>
        </div>
        <div className="slider-labels">
          <span>0%</span>
          <span>300%</span>
        </div>
      </div>

      <div className="policy-details">
        <div className="details-section">
          <h4>Policy Description</h4>
            <p>
              Increase parking pricing to reduce vehicle miles traveled. This policy uses an
              elasticity-based approach where the responsiveness to price changes depends on
              the availability of transportation alternatives. The elasticity of -0.4 (Pierce & Shoup 2013)
              was measured in San Francisco where 30.29% of commuters drive alone to work.
              Communities with fewer single-occupancy vehicle commuters have lower elasticity
              (fewer alternatives), while communities with more alternatives can see greater responses.
            </p>
          </div>

          <div className="details-section">
            <h4>Critical Assumptions</h4>
            <div className="assumption-controls">
              <div className="assumption-control">
                <label>
                  Percentage of VMT Affected by Policy
                  <span className="assumption-value">{assumptions.vmtAffectedPercent}%</span>
                </label>
                <input
                  type="range"
                  min={5}
                  max={50}
                  step={5}
                  value={assumptions.vmtAffectedPercent}
                  onChange={(e) => handleChange('vmtAffectedPercent', Number(e.target.value))}
                  className="policy-slider"
                />
                <div className="assumption-description">
                  The portion of total VMT that occurs in areas with parking pricing. Default: 15%
                </div>
              </div>

              <div className="assumption-control">
                <label>
                  Percentage Who Commute by Car Alone
                  <span className="assumption-value">{assumptions.commuteByCarPercent.toFixed(1)}%</span>
                </label>
                <input
                  type="range"
                  min={10}
                  max={100}
                  step={5}
                  value={assumptions.commuteByCarPercent}
                  onChange={(e) => handleChange('commuteByCarPercent', Number(e.target.value))}
                  className="policy-slider"
                />
                <div className="assumption-description">
                  {actualCommutePercent !== null && actualCommutePercent > 0
                    ? `The percentage of workers who drove alone to work in ${location} in 2023 was ${actualCommutePercent.toFixed(1)}%. `
                    : 'The percentage of workers who drive alone to work. '}
                  Higher percentages indicate fewer transportation alternatives, resulting in lower price elasticity. San Francisco baseline: 30.29%
                </div>
              </div>
            </div>
          </div>

          <div className="details-section">
            <h4>Calculation Results</h4>
            <div className="calculation-results">
              <div className="result-item">
                <span className="result-label">Elasticity:</span>
                <span className="result-value">{elasticity.toFixed(4)}</span>
              </div>
              <div className="result-item">
                <span className="result-label">Current VMT:</span>
                <span className="result-value">{currentVMT.toLocaleString(undefined, {maximumFractionDigits: 0})} miles</span>
              </div>
              <div className="result-item">
                <span className="result-label">Projected BAU VMT:</span>
                <span className="result-value">{projectedBAUVMT.toLocaleString(undefined, {maximumFractionDigits: 0})} miles</span>
              </div>
              <div className="result-item">
                <span className="result-label">VMT Affected:</span>
                <span className="result-value">{(projectedBAUVMT * assumptions.vmtAffectedPercent / 100).toLocaleString(undefined, {maximumFractionDigits: 0})} miles</span>
              </div>
              <div className="result-item highlight">
                <span className="result-label">VMT Reduction:</span>
                <span className="result-value">{finalResults.vmtReduction.toLocaleString(undefined, {maximumFractionDigits: 0})} miles ({finalResults.percentReduction.toFixed(2)}%)</span>
              </div>
            </div>
          </div>

          <div className="details-section">
            <h4>Literature Source</h4>
            <p>
              Pierce, G., & Shoup, D. (2013). Getting the prices right: An evaluation of pricing parking by
              demand in San Francisco. <em>Journal of the American Planning Association</em>, 79(1), 67-81.
            </p>
            <p className="literature-note">
              This study found a price elasticity of demand for parking of -0.4 in downtown San Francisco.
              This elasticity is scaled based on commute patterns, assuming communities with more
              transportation alternatives (lower single-occupancy vehicle share) will have higher elasticity.
            </p>
          </div>
      </div>
    </div>
  );
};

export default ParkingPricingPolicy;
